import { useMobile } from "@/hooks/use-responsive";
import localFileIcon from "@/assets/convert/local-file.svg";
import linkIcon from "@/assets/convert/link.svg";
import recordAudioIcon from "@/assets/convert/record-audio.svg";
import React, { useEffect, useState } from "react";
import styles from "@/components/common/input/styles/media-formats-input.module.scss";
import Image from "next/image";
import LocalFileInput, {
  LocalFileInputProps,
} from "@/components/common/input/local-file-input";
import YoutubeLinkInput from "@/components/common/input/youtube-link-input";
import RecordAudioInput from "@/components/common/input/record-audio-input";
import { useMediaInputContext } from "@/providers/media-input-provider/media-input-provider";

type MediaFormatInputId = "LocalFile" | "YouTubeLink" | "RecordAudio";
type MediaFormatTabsPosition = "top" | "bottom";
type MediaFormatInput = {
  id: MediaFormatInputId;
  title: string;
  image: string;
  isAvailable: boolean;
};

export interface MediaFormatsInputProps {
  localFileInputProps: LocalFileInputProps;
  tabsPosition?: MediaFormatTabsPosition;
}

const MediaFormatsInput = ({
  localFileInputProps,
  tabsPosition = "top",
}: MediaFormatsInputProps) => {
  const { isMobile } = useMobile();
  const { setSelectedTab, uploadedFiles } = useMediaInputContext();

  const handleSelectFormat = (id: MediaFormatInputId) => {
    setSelectedFormatId(id);
    switch (id) {
      case "YouTubeLink":
        setSelectedTab("youtube");
        break;
      case "RecordAudio":
        setSelectedTab("recordAudio");
        break;
      case "LocalFile":
        setSelectedTab("localFile");
        break;
      default:
        setSelectedTab("localFile");
        break;
    }
  };

  const mediaFormats = [
    {
      id: "LocalFile",
      title: isMobile ? "File" : "Upload Audio",
      isAvailable: true,
      image: localFileIcon,
    },
    {
      id: "YouTubeLink",
      title: isMobile ? "YouTube" : "YouTube link",
      isAvailable: true,
      image: linkIcon,
    },
    {
      id: "RecordAudio",
      title: isMobile ? "Mic" : "Record audio",
      isAvailable: true,
      image: recordAudioIcon,
    },
  ] as MediaFormatInput[];

  const getActiveTab = (): MediaFormatInputId => {
    switch (uploadedFiles.activeTab) {
      case "localFile":
        return "LocalFile";
      case "youtube":
        return "YouTubeLink";
      case "recordAudio":
        return "RecordAudio";
    }
  };

  const [selectedFormatId, setSelectedFormatId] = useState(getActiveTab());

  useEffect(() => {
    setSelectedFormatId(getActiveTab());
  }, [uploadedFiles.activeTab]);

  const tabs = (
    <div className={styles.buttons_wrapper}>
      {mediaFormats.map((format) => (
        <div key={format.id} className={styles.button_container}>
          <button
            className={`${styles.button} ${selectedFormatId === format.id ? styles.button_selected : ""}`}
            role="button"
            disabled={!format.isAvailable}
            onClick={() => handleSelectFormat(format.id)}
          >
            <div className={styles.button_content}>
              <Image src={format.image} alt="" width={20} height={20} />
              <div
                className={`${styles.button_content_text} ${selectedFormatId === format.id ? styles.button_selected_content_text : ""}`}
              >
                {format.title}
              </div>
            </div>
          </button>
          {!format.isAvailable && (
            <div className={styles.soon_wrapper}>
              <div className={styles.soon_text}>Soon</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const mediaInput = (
    <div className={styles.media_input}>
      {selectedFormatId === "LocalFile" && (
        <LocalFileInput localFileInputProps={localFileInputProps} />
      )}
      {selectedFormatId === "YouTubeLink" && <YoutubeLinkInput />}
      {selectedFormatId === "RecordAudio" && <RecordAudioInput />}
    </div>
  );

  const content =
    tabsPosition === "top" ? [tabs, mediaInput] : [mediaInput, tabs];

  return <div className={styles.wrapper}>{content}</div>;
};

export default MediaFormatsInput;
